import { Injectable, inject, resource, signal } from '@angular/core';
import { classes } from 'polytype';
import type { Observable } from 'rxjs';
import { lastValueFrom, map, tap } from 'rxjs';

import { GROUP_SUBPATH } from '@app/shared/constants/api-route.config';
import type { OBJECTS } from '@app/shared/constants/shared.constant';
import {
    type ItemResponse,
    type ListResponse,
    type StatusResponse,
    WithCrudService,
} from '@app/shared/services/mixins/crud.service';
import { WithLogService } from '@app/shared/services/mixins/log.service';
import { RestService } from '@app/shared/services/rest.service';

export class GroupAdapter {
    id?: number;
    name: string;
    description: string;
    projectSlug: string;
    objectType: OBJECTS;
    parentGroupId?: number | null;
}

@Injectable({
    providedIn: 'root',
})
export class GroupService extends classes(WithLogService, WithCrudService<GroupAdapter>) {
    protected endpoint = GROUP_SUBPATH;
    protected readonly api = inject(RestService);

    private readonly cachedData = {
        // Resources are load as they are instantiated. But we want to only trigger an HTTP call when the resource is accessed.
        // Hence using a single use signal
        start: signal(false),
        rx: resource({
            params: (): { start: boolean } => ({
                start: this.cachedData.start(),
            }),
            loader: ({ params }) => {
                if (!params.start) return null;
                return lastValueFrom(
                    this.api
                        .getWithParams<ListResponse<GroupAdapter>>(this.endpoint, {})
                        .pipe(map(({ result }) => result)),
                );
            },
        }),
    };

    isCacheLoading = this.cachedData.rx.asReadonly().isLoading;

    getDataList(
        params: Parameters<WithCrudService<GroupAdapter>['getDataList']>[0] & {
            group?: string;
            objectType?: OBJECTS;
            projectSlug?: string | null;
        },
    ): Observable<ListResponse<GroupAdapter>> {
        return this.api.getWithParams(this.endpoint, { ...params, usage: true });
    }

    getGroups() {
        this.cachedData.start.set(true);
        return this.cachedData.rx;
    }

    // Override CRUD operations to evict cache
    create(
        t: GroupAdapter,
        showError?: boolean,
        options?: any,
    ): Observable<ItemResponse<GroupAdapter>> {
        delete t.id;

        return this.api
            .post<ItemResponse<GroupAdapter>>(this.endpoint, t, showError, options)
            .pipe(tap(() => this.cachedData.rx.reload()));
    }

    update(
        t: GroupAdapter,
        showError?: boolean,
        options?: any,
    ): Observable<ItemResponse<GroupAdapter>> {
        return this.api
            .post<ItemResponse<GroupAdapter>>(`${this.endpoint}${t.id}`, t, showError, options)
            .pipe(tap(() => this.cachedData.rx.reload()));
    }

    patch(t: Partial<GroupAdapter>, showError = true): Observable<ItemResponse<GroupAdapter>> {
        return this.api
            .patch<ItemResponse<GroupAdapter>>(`${this.endpoint}${t.id}`, t, showError)
            .pipe(tap(() => this.cachedData.rx.reload()));
    }

    delete(id: number, showError = true): Observable<StatusResponse> {
        return this.api
            .delete<StatusResponse>(`${this.endpoint}${id}`, showError)
            .pipe(tap(() => this.cachedData.rx.reload()));
    }

    copy(id: number): Observable<ItemResponse<GroupAdapter>> {
        return this.api
            .post<ItemResponse<GroupAdapter>>(`${this.endpoint}${id}/copy`)
            .pipe(tap(() => this.cachedData.rx.reload()));
    }
}
